/** Sprint 5-c / item 7 · 公司會議室（實體會議·單一裝置錄整場）。
 *  主席開始/結束並指定紀錄人員；錄音裝置（紀錄人員或主席）自動連續辨識並上傳 room 逐字稿；
 *  其他與會者只報到、看即時出席，不錄音。「我的發言」per-person 模型保留給未來線上會議（不在此）。*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Avatar, Badge, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { useMessage } from '../../../contexts/MessageContext';
import { useContinuousSpeech } from '../../../hooks/useContinuousSpeech';
import {
  addChunk, checkIn, checkOut, getMeeting, liveAttendees, LiveAttendee, OfficeMeeting,
  startRecording, stopRecording,
} from './services/officeMeetingService';

const CHUNK_MS = 30000;
const POLL_MS = 5000;

const sttLangOf = (lang: string): string =>
  (lang === 'zh-CN' ? 'zh-CN' : lang === 'en' ? 'en-US' : 'zh-TW');

const OfficeMeetingRoomPage: React.FC = () => {
  const { id } = useParams();
  const mid = Number(id);
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const tt = (k: string, d: string) => t(`officeMeeting.${k}`, d);
  const { showApiError, showSuccess } = useMessage();
  const { supported, recording, interim, start, stop } = useContinuousSpeech(sttLangOf(i18n.language));

  const [m, setM] = useState<OfficeMeeting | null>(null);
  const [live, setLive] = useState<LiveAttendee[]>([]);
  const [lines, setLines] = useState<string[]>([]);
  const [startDlg, setStartDlg] = useState(false);
  const [recorderId, setRecorderId] = useState<number | ''>('');

  const bufRef = useRef<string[]>([]);
  const chunkIdxRef = useRef(0);
  const flushTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const inProgress = m?.status === 'in_progress';
  const isChair = !!m?.is_chair;
  const canRecord = !!m?.can_record;   // 我是錄音裝置（紀錄人員或主席）

  const refreshMeeting = useCallback(async () => {
    try { setM(await getMeeting(mid)); } catch (err) { showApiError(err); }
  }, [mid, showApiError]);

  const refreshLive = useCallback(async () => {
    try { setLive(await liveAttendees(mid)); } catch { /* silent ok: 輪詢出席失敗不阻擋會議室 */ }
  }, [mid]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await refreshMeeting();
      try { await checkIn(mid, navigator.userAgent); } catch (err) { showApiError(err); }
      if (mounted) refreshLive();
    })();
    pollTimer.current = setInterval(refreshLive, POLL_MS);
    return () => {
      mounted = false;
      if (pollTimer.current) clearInterval(pollTimer.current);
      if (flushTimer.current) clearInterval(flushTimer.current);
      try { stop(); } catch { /* silent ok: STT 可能已停 */ }
      checkOut(mid).catch(() => undefined);   // silent ok: 離場失敗不阻擋導航
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mid]);

  const flush = useCallback(async () => {
    const text = bufRef.current.join(' ').trim();
    if (!text) return;
    bufRef.current = [];
    try { await addChunk(mid, chunkIdxRef.current++, text, CHUNK_MS); }
    catch { /* silent ok: 單段上傳失敗·下段續傳 */ }
  }, [mid]);

  // 錄音裝置：會議進行中自動連續辨識 + 定時上傳（單一裝置錄整場）
  useEffect(() => {
    if (inProgress && canRecord && supported && !recording) {
      start((finalText) => { bufRef.current.push(finalText); setLines((prev) => [...prev, finalText]); });
      if (!flushTimer.current) flushTimer.current = setInterval(flush, CHUNK_MS);
    }
    if (!inProgress && recording) { stop(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, canRecord, supported]);

  const doStart = async () => {
    try {
      await startRecording(mid, recorderId === '' ? null : recorderId);
      setStartDlg(false);
      await refreshMeeting();
      showSuccess('SYS020001', { name: tt('recording', '會議錄音') });
    } catch (err) { showApiError(err); }
  };

  const doStop = async () => {
    stop();
    if (flushTimer.current) { clearInterval(flushTimer.current); flushTimer.current = null; }
    await flush();
    try {
      await stopRecording(mid);
      showSuccess('SYS020001', { name: tt('minutes', '會議記錄') });
      nav(`/ee_office_meeting/${mid}/minutes`);
    } catch (err) { showApiError(err); }
  };

  if (!m) return <Box sx={{ p: 3 }}><Typography color="text.secondary">{tt('loading', '載入中…')}</Typography></Box>;

  const present = live.filter((a) => a.present);
  const openStartDlg = () => { setRecorderId(m.recorder_user_id ?? ''); setStartDlg(true); };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Button size="small" onClick={() => nav(`/ee_office_meeting/${mid}`)}>← {tt('back', '返回明細')}</Button>

      <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography variant="h6">{m.title}</Typography>
          <Chip size="small" label={m.meeting_no} />
          <Chip size="small" color={inProgress ? 'warning' : 'default'} label={tt(`status_${m.status}`, m.status)} />
        </Stack>
        <Typography variant="caption" color="text.secondary">{tt('owner', '主辦')}：{m.owner_name}</Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('liveAttendees', '目前在會議室')}（{present.length}）</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {present.map((a) => {
            const speaking = m.recording_user_id === a.user_id && recording && !!interim;
            return (
              <Stack key={a.user_id} alignItems="center" sx={{ width: 72 }}>
                <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={speaking ? <FiberManualRecordIcon sx={{ fontSize: 14, color: 'error.main', animation: 'omPulse 1s infinite' }} /> : null}>
                  <Avatar sx={{ bgcolor: speaking ? 'error.main' : 'primary.main' }}>{a.name.charAt(0).toUpperCase()}</Avatar>
                </Badge>
                <Typography variant="caption" noWrap sx={{ maxWidth: 72 }}>{a.name}</Typography>
              </Stack>
            );
          })}
          {present.length === 0 && <Typography variant="body2" color="text.secondary">{tt('nobody', '（尚無人在線）')}</Typography>}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        {inProgress && m.recording_user_name && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {tt('recordingBy', '會議進行中 · 由')} {m.recording_user_name} {tt('recordingBySuffix', '記錄')}
          </Alert>
        )}
        {inProgress && canRecord && !supported && (
          <Alert severity="warning" sx={{ mb: 1 }}>{tt('noStt', '此瀏覽器不支援語音辨識')}</Alert>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          {isChair && !inProgress && m.status === 'scheduled' && (
            <Button variant="contained" color="warning" onClick={openStartDlg}>{tt('startRec', '開始會議')}</Button>
          )}
          {isChair && inProgress && (
            <Button variant="outlined" color="error" onClick={doStop}>{tt('stopRec', '結束會議並產記錄')}</Button>
          )}
        </Stack>

        {/* 即時字幕（錄音裝置） */}
        {canRecord && (lines.length > 0 || interim) && (
          <Box sx={{ mt: 2, maxHeight: 260, overflowY: 'auto', bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
            {lines.map((l, i) => <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>{l}</Typography>)}
            {interim && <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>{interim}</Typography>}
          </Box>
        )}
      </Paper>

      {/* 主席開始會議：指定紀錄人員 */}
      <Dialog open={startDlg} onClose={() => setStartDlg(false)} fullWidth maxWidth="xs">
        <DialogTitle>{tt('startTitle', '開始會議')}</DialogTitle>
        <DialogContent>
          <TextField select fullWidth sx={{ mt: 1 }} label={tt('recorder', '紀錄人員')}
            value={recorderId === '' ? '' : recorderId}
            onChange={(e) => setRecorderId(e.target.value === '' ? '' : Number(e.target.value))}
            helperText={tt('recorderHint2', '由此人的裝置錄整場；未指定則由主席的裝置')}>
            <MenuItem value="">{tt('recorderSelf', '（主席自己）')}</MenuItem>
            {(m.attendees || []).map((a) => <MenuItem key={a.user_id} value={a.user_id}>{a.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartDlg(false)}>{tt('cancel', '取消')}</Button>
          <Button variant="contained" onClick={doStart}>{tt('startConfirm', '開始')}</Button>
        </DialogActions>
      </Dialog>
      <style>{'@keyframes omPulse{0%{opacity:1}50%{opacity:.3}100%{opacity:1}}'}</style>
    </Box>
  );
};

export default OfficeMeetingRoomPage;
